/**
 * The composer's picker: Emoji · Stickers · GIFs. Tab and search state (logic/picker), the sticker sections (recents +
 * My stickers, or ranked results), the GIF search state machine (logic/gifs, debounced, client-side budget, stale
 * responses dropped) and keyboard navigation over the grids. Sending a sticker or a GIF queues a message like any
 * other (offline included); GIF searches need the network.
 */
import type { GifConfigResponse, Sticker } from "@bundle/contracts";
import {
  allowGifRequest,
  GIF_DEBOUNCE_MS,
  GIF_PAGE_SIZE,
  GIF_RATE_WINDOW_MS,
  GIF_REQUESTS_PER_MINUTE,
  gifReducer,
  initialGifState,
  klipyListUrl,
  klipyShareBody,
  klipyShareUrl,
  parseKlipyPage,
  pendingGifRequest,
  pruneGifRequests,
  type GifEvent,
  type GifResult,
  type GifSearchState,
} from "../logic/gifs.ts";
import { cellAt, currentQuery, initialPicker, pickerReducer, pickerTabs, type GridDirection, type PickerEvent, type PickerState, type PickerTab } from "../logic/picker.ts";
import { pickerSections, stickerRef, type StickerSection } from "../logic/stickers.ts";
import type { SendInput } from "../logic/outbox.ts";
import { errorKind } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";
import type { StickersViewModel } from "./StickersViewModel.ts";

/** Columns of the sticker and GIF grids (keyboard navigation uses the same numbers). */
export const STICKER_COLUMNS = 4;
export const GIF_COLUMNS = 2;
export const STICKER_RESULTS_LIMIT = 60;
const TAB_KEY = "bundle.picker.tab";

export type MediaPickerState = {
  open: boolean;
  picker: PickerState;
  /** Stickers tab content. */
  sections: StickerSection[];
  /** Item count of each section (keyboard navigation). */
  sectionSizes: number[];
  stickersLoading: boolean;
  /** The workspace has no stickers I can use yet. */
  noStickers: boolean;
  canCreate: boolean;
  /** GIFs tab content. */
  gifs: GifSearchState;
  /** The GIF config is still loading. */
  gifConfigLoading: boolean;
  /** Sending is possible here (I'm a member). */
  canSend: boolean;
};

export type PickTarget = { channelId: string; threadRootId?: string };

export class MediaPickerViewModel extends ViewModel<MediaPickerState> {
  private readonly stickers: StickersViewModel;
  private readonly target: PickTarget;
  private cancelDebounce: (() => void) | undefined;
  private sent: number[] = [];
  private canSendFn: () => boolean;
  /** Thread pickers: asked once per sticker/GIF whether it also goes to the channel (see `setAlsoSendSource`). */
  private alsoSendFn: (() => boolean) | undefined;

  /** `canSend`: may I post here (default: I'm a member of the conversation, from the workspace cache). */
  constructor(stickers: StickersViewModel, target: PickTarget, canSend?: () => boolean) {
    const tabs = pickerTabs(true, true);
    const last = stickers.ws.deps.storage.get(TAB_KEY) as PickerTab | undefined;
    super({
      open: false,
      picker: initialPicker(tabs, last),
      sections: [],
      sectionSizes: [],
      stickersLoading: stickers.state.status === "loading",
      noStickers: false,
      canCreate: stickers.state.canCreate,
      gifs: initialGifState(),
      gifConfigLoading: false,
      canSend: false,
    });
    this.stickers = stickers;
    this.target = target;
    this.canSendFn = canSend ?? (() => stickers.ws.state.cache?.memberships[target.channelId] !== undefined);
    this.onDispose(stickers.subscribe(() => this.recompute()));
    this.onDispose(stickers.ws.subscribe(() => this.update({ canSend: this.canSendFn() })));
    this.onDispose(() => this.cancelDebounce?.());
    this.recompute();
  }

  private recompute(): void {
    const s = this.stickers.state;
    // The GIFs tab stays when GIFs are unavailable: it explains why ("GIFs unavailable") instead of vanishing.
    const tabs = pickerTabs(true, true);
    let picker = this.state.picker;
    if (tabs.join() !== picker.tabs.join()) picker = { ...picker, tabs, tab: tabs.includes(picker.tab) ? picker.tab : "emoji" };
    const sections = pickerSections(s.packs, s.myPackIds, s.recent, picker.stickerQuery, STICKER_RESULTS_LIMIT);
    const sizes = sections.map((x) => x.stickers.length);
    let gifs = this.state.gifs;
    if (s.gif !== undefined) gifs = gifReducer(gifs, { type: "available", available: s.gif.available });
    else if (s.gifError) gifs = gifReducer(gifs, { type: "available", available: false });
    this.update({
      picker,
      sections,
      sectionSizes: sizes.join() === this.state.sectionSizes.join() ? this.state.sectionSizes : sizes,
      stickersLoading: s.status === "loading",
      noStickers: s.status !== "loading" && s.packs.every((p) => p.stickers.length === 0),
      canCreate: s.canCreate,
      gifs,
      gifConfigLoading: s.gif === undefined && !s.gifError,
      canSend: this.canSendFn(),
    });
    if (this.state.open && this.state.picker.tab === "gifs") this.startGifs();
  }

  // ---------- Open / tabs / search ----------

  open(): void {
    if (this.state.open) return;
    this.update({ open: true, picker: { ...this.state.picker, active: -1 } });
    void this.stickers.refresh();
    if (this.state.picker.tab === "gifs") this.startGifs();
  }

  close(): void {
    this.cancelDebounce?.();
    this.update({ open: false });
  }

  setTab(tab: PickerTab): void {
    this.dispatch({ type: "tab", tab });
  }

  cycleTab(delta: number): void {
    this.dispatch({ type: "cycle", delta });
  }

  private dispatch(e: PickerEvent): void {
    const before = this.state.picker;
    const picker = pickerReducer(before, e);
    if (picker === before) return;
    this.update({ picker });
    if (picker.tab !== before.tab) {
      this.stickers.ws.deps.storage.set(TAB_KEY, picker.tab);
      if (picker.tab === "gifs") this.startGifs();
    }
    if (picker.stickerQuery !== before.stickerQuery) this.recompute();
    if (picker.gifQuery !== before.gifQuery) this.debounceGifSearch();
  }

  /** The search field of the current tab (Stickers, GIFs). */
  setQuery(text: string): void {
    this.dispatch({ type: "query", text });
  }

  get query(): string {
    return currentQuery(this.state.picker);
  }

  // ---------- Keyboard ----------

  /** Arrow keys over the current grid (stickers in sections, GIFs in one section). */
  move(dir: GridDirection): void {
    const tab = this.state.picker.tab;
    if (tab === "stickers") this.dispatch({ type: "move", dir, sizes: this.state.sectionSizes, columns: STICKER_COLUMNS });
    else if (tab === "gifs") {
      this.dispatch({ type: "move", dir, sizes: [this.state.gifs.items.length], columns: GIF_COLUMNS });
      const active = this.state.picker.active;
      if (active >= this.state.gifs.items.length - GIF_COLUMNS) this.loadMoreGifs();
    }
  }

  /** Enter: sends the highlighted sticker/GIF. Returns true when something was sent. */
  pickActive(): boolean {
    const { tab, active } = this.state.picker;
    if (active < 0) return false;
    if (tab === "stickers") {
      const cell = cellAt(active, this.state.sectionSizes);
      const sticker = cell === undefined ? undefined : this.state.sections[cell[0]]?.stickers[cell[1]];
      if (sticker === undefined) return false;
      return this.sendSticker(sticker);
    }
    if (tab === "gifs") {
      const gif = this.state.gifs.items[active];
      return gif === undefined ? false : this.sendGif(gif);
    }
    return false;
  }

  /** Key of the highlighted cell (sticker id or GIF id), for the grid's highlight. */
  get activeKey(): string | undefined {
    const { tab, active } = this.state.picker;
    if (active < 0) return undefined;
    if (tab === "gifs") return this.state.gifs.items[active]?.ref.id;
    const cell = cellAt(active, this.state.sectionSizes);
    return cell === undefined ? undefined : this.state.sections[cell[0]]?.stickers[cell[1]]?.id;
  }

  // ---------- Sending ----------

  /**
   * Thread pickers: where to ask, once per sticker/GIF, whether it also goes to the channel — the thread composer's
   * "Also send" checkbox (`ChannelViewModel.takeAlsoSend`, which also clears it). Absent = thread only.
   */
  setAlsoSendSource(source: (() => boolean) | undefined): void {
    this.alsoSendFn = source;
  }

  private input(): SendInput {
    const ws = this.stickers.ws;
    const input: SendInput = { clientId: ws.deps.ids.next(), channelId: this.target.channelId, text: "", createdAt: ws.deps.clock.now() };
    if (this.target.threadRootId !== undefined) {
      input.threadRootId = this.target.threadRootId;
      if (this.alsoSendFn !== undefined && this.alsoSendFn()) input.alsoSendToChannel = true;
    }
    return input;
  }

  /** Queues a sticker message (works offline) and closes the picker. */
  sendSticker(sticker: Sticker): boolean {
    if (!this.canSendFn()) return false;
    const input = this.input();
    input.sticker = stickerRef(sticker);
    this.stickers.ws.deps.data.send(this.stickers.ws.wsId, input);
    this.stickers.used(sticker.id);
    this.close();
    return true;
  }

  /** Queues a GIF message, sends the provider's share ping and closes the picker. */
  sendGif(gif: GifResult): boolean {
    if (!this.canSendFn()) return false;
    const input = this.input();
    input.gif = gif.ref;
    this.stickers.ws.deps.data.send(this.stickers.ws.wsId, input);
    const config = this.stickers.state.gif;
    if (config !== undefined && config.available && config.apiKey !== undefined) {
      const klipy = klipyConfig(config);
      void this.stickers.media.gifs.postJson(klipyShareUrl(klipy, gif.ref.id), klipyShareBody(klipy, this.state.gifs.query)).catch(() => undefined);
    }
    this.close();
    return true;
  }

  // ---------- GIF search ----------

  private startGifs(): void {
    void this.stickers.loadGifConfig();
    const g = this.state.gifs;
    if (g.status === "idle") this.gif({ type: "search", query: this.state.picker.gifQuery });
  }

  private debounceGifSearch(): void {
    this.cancelDebounce?.();
    this.cancelDebounce = this.stickers.clock.setTimer(GIF_DEBOUNCE_MS, () => {
      this.cancelDebounce = undefined;
      this.gif({ type: "search", query: this.state.picker.gifQuery });
    });
  }

  loadMoreGifs(): void {
    this.gif({ type: "more" });
  }

  retryGifs(): void {
    if (this.state.gifs.status === "unavailable" && this.stickers.state.gifError) {
      void this.stickers.loadGifConfig();
      return;
    }
    this.gif({ type: "retry" });
  }

  private gif(e: GifEvent): void {
    const before = this.state.gifs;
    const gifs = gifReducer(before, e);
    if (gifs === before) return;
    this.update({ gifs });
    const req = pendingGifRequest(gifs);
    if (req !== undefined && req.requestId !== pendingGifRequest(before)?.requestId) void this.fetchGifs(req.requestId, req.query, req.cursor);
  }

  private async fetchGifs(requestId: number, query: string, cursor: string | undefined): Promise<void> {
    const config = this.stickers.state.gif;
    if (config === undefined) {
      // Wait for the config, then run the same request.
      await this.stickers.loadGifConfig();
      if (this.state.gifs.requestId !== requestId) return;
      const c = this.stickers.state.gif;
      if (c === undefined || !c.available) {
        this.gif({ type: "available", available: false });
        return;
      }
      return this.fetchGifs(requestId, query, cursor);
    }
    if (!config.available || config.apiKey === undefined) {
      this.gif({ type: "available", available: false });
      return;
    }
    const now = this.stickers.clock.now();
    this.sent = pruneGifRequests(this.sent, now, GIF_RATE_WINDOW_MS);
    if (!allowGifRequest(this.sent, now, GIF_REQUESTS_PER_MINUTE, GIF_RATE_WINDOW_MS)) {
      this.gif({ type: "failed", requestId, error: "rateLimited" });
      return;
    }
    this.sent.push(now);
    const url = klipyListUrl(klipyConfig(config), query, cursor, GIF_PAGE_SIZE, this.stickers.media.locale());
    try {
      const json = await this.stickers.media.gifs.getJson(url);
      this.gif({ type: "loaded", requestId, page: parseKlipyPage(json) });
    } catch (e) {
      const kind = errorKind(e);
      this.gif({ type: "failed", requestId, error: kind === "network" ? "network" : kind === "rateLimited" ? "rateLimited" : "server" });
    }
  }
}

function klipyConfig(c: GifConfigResponse) {
  const k: { apiKey: string; contentFilter: GifConfigResponse["contentFilter"]; customerId?: string } = { apiKey: c.apiKey ?? "", contentFilter: c.contentFilter };
  if (c.customerId !== undefined) k.customerId = c.customerId;
  return k;
}
